import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import GoogleLogin from './components/GoogleLogin';
import Sidebar from './components/Sidebar';
import { AssistantPane, AssistantProvider } from './components/AssistantDock';
import DashboardPage from './pages/DashboardPage';
import CampaignsPage from './pages/CampaignsPage';
import CampaignDetailPage from './pages/CampaignDetailPage';
import EngagementPage from './pages/EngagementPage';
import MetaPage from './pages/MetaPage';
import XPage from './pages/XPage';
import GooglePage from './pages/GooglePage';
import LinkedInPage from './pages/LinkedInPage';
import ChatGPTPage from './pages/ChatGPTPage';
import WebsitePage from './pages/WebsitePage';
import AlbumPage from './pages/AlbumPage';
import TemplatesPage from './pages/TemplatesPage';
import TemplateSendsPage from './pages/TemplateSendsPage';
import CadencesPage from './pages/CadencesPage';
import CadenceDetailPage from './pages/CadenceDetailPage';

/** /campaigns/:id → /whatsapp/:id, keeping the id. */
const CampaignRedirect: React.FC = () => {
  const { campaignId } = useParams();
  return <Navigate to={`/whatsapp/${campaignId}`} replace />;
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  return isAuthenticated
    ? (
      <div className="flex h-screen">
        <Sidebar />
        <AssistantPane>{children}</AssistantPane>
      </div>
    )
    : <Navigate to="/login" state={{ from: location }} replace />;
};

const LoginRoute: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string; search: string } } | null)?.from;
  const dest = from ? `${from.pathname}${from.search}` : '/';
  return isAuthenticated ? <Navigate to={dest} replace /> : <GoogleLogin />;
};

const App: React.FC = () => (
  <AuthProvider>
    <Router>
      <AssistantProvider>
      <Routes>
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/whatsapp" element={<ProtectedRoute><CampaignsPage /></ProtectedRoute>} />
        <Route path="/whatsapp/:campaignId" element={<ProtectedRoute><CampaignDetailPage /></ProtectedRoute>} />
        {/* Old address of the broadcast list — links in chats and docs still point here. */}
        <Route path="/campaigns" element={<Navigate to="/whatsapp" replace />} />
        <Route path="/campaigns/:campaignId" element={<CampaignRedirect />} />
        <Route path="/engagement" element={<ProtectedRoute><EngagementPage /></ProtectedRoute>} />
        <Route path="/meta" element={<ProtectedRoute><MetaPage /></ProtectedRoute>} />
        <Route path="/x" element={<ProtectedRoute><XPage /></ProtectedRoute>} />
        <Route path="/google" element={<ProtectedRoute><GooglePage /></ProtectedRoute>} />
        <Route path="/linkedin" element={<ProtectedRoute><LinkedInPage /></ProtectedRoute>} />
        <Route path="/chatgpt" element={<ProtectedRoute><ChatGPTPage /></ProtectedRoute>} />
        <Route path="/website" element={<ProtectedRoute><WebsitePage /></ProtectedRoute>} />
        <Route path="/album" element={<ProtectedRoute><AlbumPage /></ProtectedRoute>} />
        <Route path="/templates" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
        <Route path="/templates/:templateId" element={<ProtectedRoute><TemplateSendsPage /></ProtectedRoute>} />
        <Route path="/cadences" element={<ProtectedRoute><CadencesPage /></ProtectedRoute>} />
        <Route path="/cadences/:cadenceId" element={<ProtectedRoute><CadenceDetailPage /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </AssistantProvider>
    </Router>
  </AuthProvider>
);

export default App;
